import { createGqlFetcher } from 'app/lib/gqlFetcher'
import { ArrowPathIcon } from '@heroicons/react/20/solid'
import { withZod } from '@remix-validated-form/with-zod'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'urql'
import { z } from 'zod'
import { AddNewIntegrationModal } from '../AddNewIntegrationModal'
import { SIKKA_OFFICES_QUERY } from '../graphql'
import { SAVE_SIKKA_INTEGRATION, REMOVE_LOCATION_INTEGRATION } from '../ops-graphql'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormCombo from 'app/components/form/FormCombo'
import FormComboAsync from 'app/components/form/FormComboAsync'
import FormSelect from 'app/components/form/FormComboSelect'
import { SmartForm } from 'app/components/form/SmartForm'
import Spinner from 'app/components/Spinner'
import { Tooltip } from 'app/components/Tooltip'
import type { SikkaOffice } from 'app/generated/graphql'
import type { LocationJson, LocationType } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { gqlOpsMutation } from 'app/lib/opsGraphqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'

const createSchema = z.object({
  locationId: z.string().min(1, { message: `A Location is required` }),
  selection: z.string().min(1, { message: `A Practice is required` }),
})

export const createSikkaValidator = withZod(createSchema)

type SikkaOfficesQueryResult = {
  integrationOptions: {
    sikkaOffices: SikkaOffice[]
  }
}

export function SikkaFormModal({
  onAdd,
  currentValues,
}: {
  onAdd: () => void
  currentValues?: {
    locationId: string
    locationName: string
    sikkaOfficeId: LocationType[`sikkaOfficeId`]
    sikkaPracticeId: LocationType[`sikkaPracticeId`]
  }
}) {
  const [selectedLocation, setSelectedLocation] = useState<OptionType | undefined>()
  const [selectedOption, setSelectedOption] = useState<OptionType | undefined>(undefined)
  const [refreshing, setRefreshing] = useState(false)
  const [confirm] = useConfirmation()

  const [{ data, fetching, error }, reexecuteQuery] = useQuery<SikkaOfficesQueryResult>({
    query: SIKKA_OFFICES_QUERY,
    variables: { locationId: currentValues?.locationId },
    requestPolicy: `network-only`,
  })

  const options = useMemo<OptionType[]>(() => {
    if (!data?.integrationOptions?.sikkaOffices) return []
    return data.integrationOptions.sikkaOffices.flatMap((office) =>
      office.practices.map((practice) => ({
        id: `${office.id}-${practice.id}`,
        name: `${office.id}-${practice.id} - ${practice.name}`,
        value: `${office.id}-${practice.id}`,
        meta: {
          isUsed: practice.isUsed,
          tooltipForDisabledOption: `This practice is connected to another location`,
          type: `practice`,
        },
      })),
    )
  }, [data])

  // Handle errors and clear refreshing state
  useEffect(() => {
    if (error) {
      logError(`Failed to load Sikka options:`, error)
      notifyError(`There was a problem loading options. You may need to reload the page.`)
    }
    if (!fetching) setRefreshing(false)
  }, [error, fetching])

  // Set selected option when data loads and we have current values
  useEffect(() => {
    if (data?.integrationOptions?.sikkaOffices && currentValues?.sikkaOfficeId && currentValues?.sikkaPracticeId) {
      const office = data.integrationOptions.sikkaOffices.find((o) => o.id === currentValues.sikkaOfficeId)
      const practice = office?.practices.find((p) => p.id === currentValues.sikkaPracticeId)
      if (office && practice) {
        setSelectedOption({
          id: `${office.id}-${practice.id}`,
          name: `${office.id}-${practice.id} - ${practice.name}`,
          value: `${office.id}-${practice.id}`,
          meta: { type: `practice` },
        })
      }
    }
  }, [data, currentValues?.sikkaOfficeId, currentValues?.sikkaPracticeId])

  const refresh = () => {
    setRefreshing(true)
    reexecuteQuery({ requestPolicy: `network-only` })
  }

  const onSave = async () => {
    const locationId = currentValues?.locationId || (selectedLocation?.value as string)
    const selection = selectedOption?.value as string
    if (!locationId || !selection) return

    const result = await gqlOpsMutation(SAVE_SIKKA_INTEGRATION, { locationId, selection })
    const data = result.data?.saveSikkaIntegration
    if (data?.success) {
      notifySuccess(`Sikka integration saved`)
      onAdd()
      closeModal()
    } else {
      notifyError(`Failed to save integration: ${data?.error || result.error || 'Unknown error'}`)
    }
  }

  const goBackToSelectIntegrationModal = () => {
    closeModal()
    showModal(
      <AddNewIntegrationModal onIntegrationAdded={onAdd} />,
      {
        title: `Add New Integration`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
      false,
    )
  }

  const onRemove = () => {
    return confirm({
      title: `Remove Sikka Integration`,
      description: `Removing this integration will also remove all associated data. This may result in contacts being removed. Do you want to continue?`,
      variant: `danger`,
    }).then(() =>
      gqlOpsMutation(REMOVE_LOCATION_INTEGRATION, {
        locationId: currentValues?.locationId!,
        integration: `sikka`,
      }).then((result) => {
        if (result.data?.removeLocationIntegration?.success) {
          notifySuccess(`Sikka integration removed`)
          onAdd()
          closeModal()
        } else {
          notifyError(`Failed to remove integration: ${result.error || 'Unknown error'}`)
        }
      }),
    )
  }

  return (
    <>
      <SmartForm
        id="integration-form"
        className="!mt-0 w-[600px] grow-0 justify-start !gap-y-4 px-6 py-4"
        validator={createSikkaValidator}
        reloadDocument={false}
        preventSubmit={true}
        onSubmit={onSave}
        hideButtons={true}
        method={currentValues ? `PUT` : `POST`}
        hiddenInputs={currentValues ? [{ name: `locationId`, value: currentValues.locationId }] : undefined}
      >
        {currentValues ? (
          <FormSelect
            cols={5}
            name="locationId"
            label="Trackable Location"
            options={[{ label: currentValues.locationName, value: currentValues.locationId }]}
            value={currentValues.locationId}
            disabled
            labelClassName="text-body-sm text-strong"
          />
        ) : (
          <FormComboAsync
            cols={5}
            name="locationId"
            required={true}
            showSearchInDropdown={true}
            label="Trackable Location"
            value={selectedLocation}
            optionsMapper={(res) =>
              res?.hits?.map((hit: LocationJson) => ({
                id: hit.id || hit.name,
                name: hit.name,
                value: hit.id,
                meta: {
                  isUsed: !!hit.sikkaPracticeId || !!hit.sikkaOfficeId,
                  tooltipForDisabledOption: `This location is already connected to Sikka`,
                },
              }))
            }
            fetcher={createGqlFetcher('locations')}
            labelClassName="text-body-sm text-strong"
            onChange={(val) => setSelectedLocation(val)}
            newLineError={false}
            showErrorIcon
          />
        )}
        {fetching ? (
          <div className="col-span-5 flex items-center pt-5">
            <Spinner size={5} />
          </div>
        ) : error ? (
          <div className="col-span-5 pt-5 text-sm text-red-600">Failed to load practices</div>
        ) : options.length === 0 ? (
          <div className="col-span-5 pt-5 text-sm text-subtle">No practices available</div>
        ) : (
          <FormCombo
            cols={5}
            name="selection"
            label="Practice"
            value={selectedOption}
            options={options}
            labelClassName="text-body-sm text-strong"
            onChange={(option) => setSelectedOption(option)}
            disabled={!!currentValues}
            showSearchInDropdown={true}
            newLineError={false}
            showErrorIcon
          />
        )}
        <div className="col-span-1 flex items-center justify-center pt-5">
          <ArrowPathIcon
            className={classNames(`ml-2 h-5 w-5 cursor-pointer`, refreshing && `animate-spin`)}
            onClick={refresh}
          />
        </div>
      </SmartForm>
      <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            {currentValues ? <Button label="Remove" mode="danger" size="md" onClick={() => onRemove()} /> : null}
          </div>
          <div className="flex gap-2">
            {!currentValues ? (
              <Button label="Back" mode="white" size="md" onClick={() => goBackToSelectIntegrationModal()} />
            ) : (
              <Button label="Cancel" mode="white" size="md" onClick={() => closeModal()} />
            )}
            <Tooltip
              message={
                currentValues
                  ? `Modifying sikka integration is not permitted. Please delete the integration and create a new one.`
                  : undefined
              }
              className={currentValues ? `cursor-not-allowed` : ``}
            >
              <Button
                type="submit"
                form="integration-form"
                label="Save"
                mode="primary"
                size="md"
                disabled={
                  !!currentValues?.sikkaPracticeId ||
                  (!selectedLocation && !currentValues?.locationId) ||
                  !selectedOption
                }
              />
            </Tooltip>
          </div>
        </div>
      </div>
    </>
  )
}
